import React from 'react';

const WIDGET_SCRIPT = 'https://platform.twitter.com/widgets.js';
let widgetRequest;

const loadWidgets = () => {
    if (window.twttr?.widgets?.createTweet) {
        return Promise.resolve(window.twttr.widgets);
    }
    if (!widgetRequest) {
        widgetRequest = new Promise((resolve, reject) => {
            let script = document.querySelector(`script[src="${WIDGET_SCRIPT}"]`);
            const owned = !script;
            if (owned) {
                script = document.createElement('script');
                script.src = WIDGET_SCRIPT;
                script.async = true;
            }
            const finish = error => {
                clearTimeout(timer);
                script.removeEventListener('load', onLoad);
                script.removeEventListener('error', onError);
                if (error) {
                    if (owned) script.remove();
                    reject(error);
                } else {
                    resolve(window.twttr.widgets);
                }
            };
            const onLoad = () => finish(window.twttr?.widgets?.createTweet ? null : new Error('Widgets unavailable'));
            const onError = () => finish(new Error('Widgets unavailable'));
            const timer = setTimeout(onError, 15000);
            script.addEventListener('load', onLoad);
            script.addEventListener('error', onError);
            if (owned) document.head.appendChild(script);
        }).catch(error => {
            widgetRequest = undefined;
            throw error;
        });
    }
    return widgetRequest;
};

export const getArticleTweetId = href => {
    try {
        const url = new URL(href);
        if (url.protocol !== 'https:' || !['x.com', 'twitter.com', 'www.x.com', 'www.twitter.com'].includes(url.hostname)) {
            return null;
        }
        return url.pathname.match(/^\/(?:i|[\w]+)\/status\/(\d+)\/?$/)?.[1] || null;
    } catch {
        return null;
    }
};

const ArticleTweetEmbed = ({ tweetId }) => {
    const container = React.useRef(null);
    React.useEffect(() => {
        let cancelled = false;
        // Each effect owns its mount so late widget completion cannot mutate a newer embed.
        const mount = document.createElement('div');
        container.current.appendChild(mount);
        loadWidgets().then(widgets => {
            if (!cancelled) {
                return widgets.createTweet(tweetId, mount, { theme: 'dark', dnt: true, align: 'center' });
            }
            return null;
        }).catch(() => {}); // The source link stays available on network/private/deleted-post failures.
        return () => {
            cancelled = true;
            mount.remove();
        };
    }, [tweetId]);

    const href = `https://x.com/i/status/${tweetId}`;
    return (
        <div style={{ margin: '18px 0', maxWidth: '100%' }}>
            <div ref={container} />
            <a href={href} target="_blank" rel="noopener noreferrer">{href}</a>
        </div>
    );
};

// Only importer-generated standalone markers become embeds; ordinary links stay links.
export const ArticleReaderParagraph = ({ children, ...props }) => {
    const items = React.Children.toArray(children).filter(child => typeof child !== 'string' || child.trim());
    const link = items.length === 1 && React.isValidElement(items[0]) ? items[0] : null;
    const label = link && React.Children.toArray(link.props.children).join('');
    const tweetId = label === 'Embedded post' ? getArticleTweetId(link.props.href) : null;
    return tweetId ? <ArticleTweetEmbed tweetId={tweetId} /> : <p {...props}>{children}</p>;
};

export default ArticleTweetEmbed;
